import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from 'api';
import UnitConvert from 'UnitConverter';
import Util from 'utility';
import { openModal, closeModal, sortFood, removeFood, setFoodAmount, moveFoodToPlate, setFoodAsDrink } from '../../../actions/index';
import LectureTaskPanelFoodModifications from './LectureTaskPanelFoodModifications';
import { __ } from 'locale';

function FoodItem({ food: f, mealHash: m, focusedAmountInputId, focusedAmountInputValue, onAmountFocus, onAmountChange, onAmountBlur, onSave, onSaveAndUpdate }) {
    const dispatch = useDispatch();
    const globals = useSelector(state => state.globals);
    const modifications = useSelector(state => state.modifications);
    const appModeData = useSelector(state => state.appModeData);
    const appMode = useSelector(state => state.appMode);
    const allFoodGroups = useSelector(state => state.foodGroups);
    const allMeals = useSelector(state => state.meals);
    const moveFoodRef = useRef({ foodId: 0, targetId: '' });

    const handleFoodAmountBlur = (e) => {
        let weight = e.target.value.replace(',', '.') * 1;
        const item = globals.itemData[f.food_id];
        if (isNaN(weight) || weight < item.weight_min || weight > item.weight_max) {
            alert("Neplatné množství potraviny - zadejte prosím množství od " + item.weight_min + " do " + item.weight_max + " " + item.weight_unit);
            weight = item.weight;
        }
        onAmountBlur();
        dispatch(setFoodAmount({ mealHash: m, foodId: f.id, weight }));
        onSaveAndUpdate();
    };

    const handleDrinkChange = (e) => {
        dispatch(setFoodAsDrink({ mealHash: m, foodId: f.id, is_drink: e.currentTarget.checked ? 1 : 0 }));
        onSaveAndUpdate();
    };

    const handleSort = (direction) => {
        dispatch(sortFood({ foodId: f.id, direction, mealHash: m }));
        onSave();
    };

    const handleRemove = () => {
        dispatch(removeFood({ id: f.id, mealHash: m }));
        onSaveAndUpdate();
    };

    const openFoodModificationsPanel = (e) => {
        e.preventDefault();
        dispatch(closeModal());
        dispatch(openModal({ title: 'Úpravy potraviny ' + f.title, class: 'dialog-flexible', content: <LectureTaskPanelFoodModifications food={f} /> }));
    };

    const loadFoodDescription = (amount) => {
        const amt = Math.round(amount * 1000);
        api('food/description-lt/' + f.food_id + '/' + amt + '/' + appModeData.lectureId).then(result => {
            result = result.data;
            if (result.error) return false;
            dispatch(closeModal());
            dispatch(openModal({
                title: result.title,
                class: 'dialog dialog-flexible',
                content: <div className="p-2"><div dangerouslySetInnerHTML={{ __html: result.description }} /></div>
            }));
        });
    };

    const showAlergenInfo = (alerg) => {
        let modelTitle = 'Alergen';
        if(Util.getLang() == 'sk') modelTitle = 'Alergén';
        dispatch(openModal({ title: modelTitle, content: <div className="p-2"><p>{alerg}</p></div> }));
    };

    const doMoveFoodToPlate = () => {
        if (moveFoodRef.current.targetId === '') return;
        const { foodId, targetId, targetMealHash } = moveFoodRef.current;
        dispatch(moveFoodToPlate({ sourceMealHash: m, mealHash: targetMealHash, foodId, foodGroupId: targetId }));
        dispatch(closeModal());
        onSaveAndUpdate();
    };

    const moveFoodToAnotherGroup = () => {
        moveFoodRef.current = { foodId: f.id, targetId: '', targetMealHash: m };

        const unnamedTitles = { plate: 'Nepojmenovaný talíř', bowl: 'Nepojmenovaná miska', cup: 'Nepojmenovaný šálek', glass: 'Nepojmenovaná sklenice' };
        const foodGroupsOptions = [<option value="" key="empty">Vyberte...</option>];

        Object.keys(allMeals).forEach(mealHash => {
            const meal = allMeals[mealHash];
            const groups = allFoodGroups[mealHash];
            if (!groups) return;
            const mealLabel = globals.mealTypes[meal.mealId]?.title || mealHash;
            Object.keys(groups).forEach(g => {
                const group = groups[g];
                const fgTitle = group.title || unnamedTitles[group.type] || '';
                const label = `${mealLabel} – ${fgTitle}`;
                foodGroupsOptions.push(
                    <option value={g} key={`${mealHash}-${g}`} data-meal-hash={mealHash}>{label}</option>
                );
            });
        });

        dispatch(closeModal());
        dispatch(openModal({
            title: __('Přesunout potravinu'),
            content: (
                <div className="p-2">
                    <p className="mb-3 text-sm">{__('Přesunout potravinu')} <b>{f.title}</b> {__('do')}:</p>
                    <select className="w-full p-2 mb-4 border border-slate-200 rounded-lg text-sm" onChange={e => {
                        const opt = e.target.options[e.target.selectedIndex];
                        moveFoodRef.current.targetId = e.target.value;
                        moveFoodRef.current.targetMealHash = opt.dataset.mealHash || m;
                    }}>
                        {foodGroupsOptions}
                    </select>
                    <div className="flex items-center gap-4">
                        <button className="bg-green-500 text-white py-2 px-4 rounded-lg text-sm font-medium cursor-pointer border-none hover:bg-green-600 transition-colors" onClick={doMoveFoodToPlate}>
                            {__('Přesunout')}
                        </button>
                        <a className="text-slate-400 underline cursor-pointer text-sm" onClick={() => dispatch(closeModal())}>{__('Zpět')}</a>
                    </div>
                </div>
            )
        }));
    };

    // Normalized unit display
    const foodItemData = {
        id: f.food_id,
        selectedWeight: f.weight,
        weight: globals.itemData[f.food_id].weight,
        norm_unit_amount: globals.itemData[f.food_id].norm_unit_amount * 1.0,
        norm_unit_unit: globals.itemData[f.food_id].norm_unit_unit,
        norm_unit2_amount: globals.itemData[f.food_id].norm_unit2_amount * 1.0,
        norm_unit2_unit: globals.itemData[f.food_id].norm_unit2_unit,
    };
    let normalizedAmountInfo = UnitConvert.convert(foodItemData);
    normalizedAmountInfo = (normalizedAmountInfo && normalizedAmountInfo !== ' null')
        ? <span className="text-slate-400">({normalizedAmountInfo})</span>
        : false;

    const inputValue = focusedAmountInputId === f.id ? focusedAmountInputValue : parseFloat(f.weight + '');

    // Drink mode checkbox
    let drinkModeSelection = false;
    if (globals.itemData[f.food_id].is_drink == 1) {
        drinkModeSelection = (
            <label className="flex items-center gap-1 text-xs text-slate-400 cursor-pointer ml-1">
                <input className="rounded" type="checkbox" name={'is_drink_' + f.food_id} onChange={handleDrinkChange} checked={f.is_drink == 1} />
                pitný režim
            </label>
        );
    }

    // Allergens + undesirable foods
    const watchAlergens = Array.isArray(appModeData.watchAlergens) ? appModeData.watchAlergens.map(a => +a) : [];
    let alergenList = [];
    if (typeof globals.foodAlergens[f.food_id] === 'object') {
        globals.foodAlergens[f.food_id].slice().sort().forEach(id => {
            const fullName = globals.itemAlergens[id];
            const shortName = fullName.split('-')[0].trim();
            const isWatched = watchAlergens.includes(id * 1);
            alergenList.push(
                <span
                    key={id}
                    onClick={() => showAlergenInfo(fullName)}
                    className={`cursor-pointer underline decoration-dotted ${isWatched ? 'text-red-500' : 'text-slate-400'}`}
                >
                    {shortName}
                </span>
            );
            alergenList.push(<span key={id + '_sep'} className="text-slate-300">, </span>);
        });
    }

    let undesirableInfo = false;
    const showUndesirableWarning = !Util.isNutriKucharMode(appMode);
    const isUndesirable = typeof appModeData.undesirableFoods !== 'undefined' && appModeData.undesirableFoods.includes(f.food_id);
    if (alergenList.length > 1) {
        alergenList.pop();
        if (showUndesirableWarning && isUndesirable) {
            undesirableInfo = (
                <div className="flex items-center gap-1 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                        <i className="fa fa-exclamation-triangle text-[9px]"></i>
                        Nevhodná potravina:
                    </span>
                    <span className="text-xs text-slate-500">{alergenList}</span>
                </div>
            );
        } else {
            undesirableInfo = (
                <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-slate-400 font-medium">{__('Alergeny')}:</span>
                    <span className="text-xs text-slate-500">{alergenList}</span>
                </div>
            );
        }
    } else if (showUndesirableWarning && isUndesirable) {
        undesirableInfo = (
            <div className="mt-1">
                <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                    <i className="fa fa-exclamation-triangle text-[9px]"></i>
                    Nevhodná potravina
                </span>
            </div>
        );
    }

    // Modifications badge
    let modList = [];
    if (typeof modifications[f.id] !== 'undefined') {
        modifications[f.id].forEach(key => modList.push(globals.preparation_types[key]));
    }
    const hasModifications = modList.length > 0;
    const modListStr = hasModifications ? modList.join(', ') : 'Bez úprav';
    const hideMoveFoodButton = Util.isNutriKucharMode(appMode) && Util.isSingleDishLectureTask(appModeData?.lectureInfoData);

    return (
        <div className="group flex items-start gap-3 py-3 px-4 border-b border-slate-100 hover:bg-slate-50/60 transition-colors last:mb-2">
            {/* Food image */}
            <div
                className="w-11 h-11 shrink-0 rounded-lg bg-center bg-no-repeat bg-contain bg-slate-50 border border-slate-100 mt-0.5"
                style={{ backgroundImage: `url(${f.image})` }}
            />

            {/* Content */}
            <div className="flex-1 min-w-0">
                {/* Title row */}
                <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-1.5">
                    <h4 className="font-semibold text-sm text-slate-800 m-0 leading-snug">{f.title}</h4>
                    {drinkModeSelection}
                </div>

                {/* Amount row */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <input
                            className="w-16 text-right px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white shadow-sm focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-100 font-medium text-slate-700"
                            type="text"
                            name="weight"
                            value={inputValue}
                            onChange={onAmountChange}
                            onFocus={e => onAmountFocus(e, f)}
                            onBlur={handleFoodAmountBlur}
                        />
                        <span className="text-xs text-slate-500 font-medium">{globals.itemData[f.food_id].weight_unit}</span>
                        {normalizedAmountInfo && (
                            <span className="text-xs text-slate-400">{normalizedAmountInfo}</span>
                        )}
                    </div>

                    {/* Separator dot */}
                    <span className="text-slate-300 text-xs select-none">·</span>

                    {/* Modifications badge */}
                    <button
                        type="button"
                        data-food-id={f.id}
                        onClick={openFoodModificationsPanel}
                        className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border transition-colors cursor-pointer
                            ${hasModifications
                                ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                                : 'bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                            }`}
                    >
                        {hasModifications && <i className="fa fa-fire text-[9px]"></i>}
                        {modListStr}
                    </button>
                </div>

                {/* Allergens / undesirable */}
                {undesirableInfo}
            </div>

            {/* Action buttons — fade in on hover */}
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5">
                <button
                    type="button"
                    onClick={() => handleSort(-1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title="Přesunout výše"
                >
                    <i className="fa fa-arrow-up text-xs"></i>
                </button>
                <button
                    type="button"
                    onClick={() => handleSort(1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title="Přesunout níže"
                >
                    <i className="fa fa-arrow-down text-xs"></i>
                </button>
                <button
                    type="button"
                    onClick={() => loadFoodDescription(inputValue)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title="Informace o potravině"
                >
                    <i className="fa fa-question text-xs"></i>
                </button>
                {!hideMoveFoodButton && (
                    <button
                        type="button"
                        onClick={moveFoodToAnotherGroup}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        title="Přesunout na jiný talíř"
                    >
                        <i className="fa fa-external-link-alt text-xs"></i>
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleRemove}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Odebrat potravinu"
                >
                    <i className="fa fa-trash-alt text-xs"></i>
                </button>
            </div>
        </div>
    );
}

export default FoodItem;
